import { datadogLogs } from 'src/config/datadog';
import { datadogRum } from '@datadog/browser-rum';

export const setGlobalContext = (key, value) => {
  if (datadogLogs) {
    datadogLogs.setGlobalContextProperty(key, value);
  }
};

export const setDatadogUser = (agent, originalAgent) => {
  if (!agent) return null;

  const isUserImpersonated = agent?.email !== originalAgent?.email;

  const userContext = {
    agent_id: agent?.id,
    agency_id: agent?.agency?.id,
    user_impersonated: isUserImpersonated,
  };

  if (datadogLogs) {
    Object.entries(userContext).forEach(([key, value]) => {
      datadogLogs.setGlobalContextProperty(key, value);
    });
  }

  if (datadogRum) {
    Object.entries(userContext).forEach(([key, value]) => {
      datadogRum.setGlobalContextProperty(key, value);
    });
  }

  return null;
};

export const shouldLogError = (error, context = {}) => {
  if (!error) return false;

  const errorObj = typeof error === 'string' ? new Error(error) : error;

  const requestUrl = context.url || window.location.href || '';
  const isBuggyBrowser = navigator.userAgent?.includes('Chrome/102');
  const isCypressBrowser = navigator.userAgent?.includes('Cypress');

  const isLocalDownloaded =
    requestUrl.startsWith('file://') || /^[a-zA-Z]{1}:\/\//.test(requestUrl);
  const isErrorWithStacktrace = errorObj.stack && errorObj.stack.length > 0;

  if (isLocalDownloaded) {
    return false;
  }

  if (isBuggyBrowser && !isErrorWithStacktrace) {
    return false;
  }

  const isRequestAborted =
    errorObj.name === 'Error' &&
    (errorObj.message?.includes('Request aborted') ||
      errorObj.message?.includes('AbortError'));

  if (isRequestAborted) {
    return false;
  }

  if (
    isCypressBrowser &&
    errorObj.name?.includes('s(frame-modern)') &&
    errorObj.message?.includes(
      `Cannot read properties of null (reading 'document')`,
    )
  ) {
    return false;
  }

  const chunkLoadingErrors = [
    'ChunkLoadError',
    'Loading CSS',
    'Loading chunk',
    'Could not load "util".',
    'Unable to preload CSS for',
  ];

  if (
    chunkLoadingErrors.some(
      pattern =>
        errorObj.message?.includes(pattern) || errorObj.name?.includes(pattern),
    )
  ) {
    return false;
  }

  return true;
};

export const logError = (error, context = {}, level = 'error') => {
  if (!shouldLogError(error, context)) {
    return;
  }

  if (typeof error === 'string') {
    const errorAttributes = {
      'error.message': error,
      'error.kind': 'String',
      user_agent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      ...context,
    };

    if (datadogLogs && datadogLogs.logger) {
      datadogLogs.logger[level](error, errorAttributes);
    }

    if (datadogRum && typeof datadogRum.addError === 'function') {
      datadogRum.addError(error, context);
    }
  } else {
    // Handle Error objects (like from React Error Boundaries)
    const errorAttributes = {
      'error.stack': error.stack,
      'error.message': error.message,
      'error.kind': error.name || error.constructor.name,
      user_agent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      ...context,
    };

    if (datadogLogs && datadogLogs.logger) {
      datadogLogs.logger[level](
        `Frontend Error: ${error.message}`,
        errorAttributes,
        error,
      );
    }

    if (datadogRum && typeof datadogRum.addError === 'function') {
      datadogRum.addError(error.message, {
        stack: error.stack,
        name: error.name,
        ...context,
      });
    }
  }
};

/**
 * setup global error handlers to replaces Sentry's global error handling)
 */
export const setupGlobalErrorHandling = () => {
  window.addEventListener('unhandledrejection', event => {
    logError(
      new Error(event.reason?.message || 'Unhandled Promise Rejection'),
      {
        type: 'unhandled_promise_rejection',
        reason: event.reason,
      },
    );
  });

  window.addEventListener('error', event => {
    logError(event.error || new Error(event.message), {
      type: 'javascript_error',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener(
    'error',
    event => {
      if (event.target && event.target !== window) {
        logError(
          new Error(
            `Failed to load resource: ${event.target.src || event.target.href}`,
          ),
          {
            type: 'resource_error',
            element: event.target.tagName,
            source: event.target.src || event.target.href,
          },
        );
      }
    },
    true,
  );
};

export const setTag = setGlobalContext;
export const captureException = logError;
